import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Error codes the backend returns in {"error": code} for the google-calendar endpoints.
// Anything else falls back to the generic message the caller passes in.
const KNOWN_ERROR_CODES = [
  "not_configured",
  "beta_not_enabled",
  "not_admin",
  "not_connected",
  "calendar_not_selected",
  "missing_code",
  "missing_calendar_id",
  "oauth_failed",
  "missing_scope",
  "auth_failed",
  "api_error",
];

/**
 * Translate a google-calendar API error into user-facing text. Never shows the raw
 * server message.
 */
export function googleCalendarErrorMessage(err, t, fallbackKey) {
  const code = err?.response?.data?.error;
  if (typeof code === "string" && KNOWN_ERROR_CODES.includes(code)) {
    return t(`googleCalendar.errors.${code}`);
  }
  return t(fallbackKey);
}

/**
 * Runs the Google OAuth popup flow for an organization.
 *
 * The popup lands on /auth/google-calendar/callback (same origin), which posts
 * {type: "google_calendar_callback", code, state} back to this window. Only messages from our
 * own origin whose state matches the organization the flow was started for are accepted, so a
 * stray message cannot attach a Google account to whichever organization happens to be
 * selected.
 *
 * Returns { connect, connecting, error, clearError }. connect() resolves to true when the
 * backend confirmed the connection; onConnected (optional, may be async) runs before that.
 */
export function useGoogleCalendarConnect({ organizationId, t, onConnected }) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const cleanupRef = useRef(null);

  // Drop the message listener / popup poller if the component unmounts mid-flow.
  useEffect(() => () => cleanupRef.current?.(), []);

  const connect = useCallback(async () => {
    if (!organizationId) return false;

    setConnecting(true);
    setError("");

    let popup = null;
    try {
      const response = await axios.post(`/api/organizations/${organizationId}/google-calendar/connect/`);
      const { authorization_url: authorizationUrl } = response.data;

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popup = window.open(
        authorizationUrl,
        "google_calendar_auth",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );
    } catch (err) {
      console.error("Failed to start Google Calendar OAuth:", err);
      setError(googleCalendarErrorMessage(err, t, "googleCalendar.errorConnecting"));
      setConnecting(false);
      return false;
    }

    return new Promise((resolve) => {
      let settled = false;
      let received = false;

      const cleanup = () => {
        window.removeEventListener("message", handleMessage);
        clearInterval(popupPoll);
        cleanupRef.current = null;
      };

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        cleanup();
        setConnecting(false);
        resolve(ok);
      };

      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        const data = event.data;
        if (data?.type !== "google_calendar_callback") return;
        if (String(data.state ?? "") !== String(organizationId)) return;

        received = true;
        popup?.close();

        if (data.error) {
          setError(t("googleCalendar.authError"));
          finish(false);
          return;
        }
        if (!data.code) {
          finish(false);
          return;
        }

        try {
          await axios.post(`/api/organizations/${organizationId}/google-calendar/callback/`, { code: data.code });
          if (onConnected) await onConnected();
          finish(true);
        } catch (err) {
          console.error("Failed to complete Google Calendar OAuth:", err);
          setError(googleCalendarErrorMessage(err, t, "googleCalendar.errorConnecting"));
          finish(false);
        }
      };

      // The callback page closes itself; only treat a closed popup as "cancelled" when no
      // callback message has arrived (the message handler may still be awaiting the backend).
      const popupPoll = setInterval(() => {
        if (!received && (!popup || popup.closed)) finish(false);
      }, 500);

      cleanupRef.current = cleanup;
      window.addEventListener("message", handleMessage);
    });
  }, [organizationId, t, onConnected]);

  const clearError = useCallback(() => setError(""), []);

  return { connect, connecting, error, clearError };
}
